import { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Search } from "lucide-react";
import { useLetterhead } from "./hooks/useLetterhead.ts";
import { TemplateCard } from "./components/template";
import { LetterCard, LetterViewModal } from "./components/index.js";
import { getLetters } from "../../../Redux/Reducers/letterHeadReducer.ts";
import { useUserDetailContext } from "../../../Components/UserDetailContext.js";
// import { HeaderCompo } from "../../components/layout";

const LetterheadMainPage = () => {
    const dispatch = useDispatch();
    const {
        activeTab,
        selectedLetter,
        templates,
        letters,
        isLoading,
        setActiveTab,
        handleViewLetter,
        handleCloseModal,
        handleNavigate,
        handleDeleteLetter,
        handleDeleteTemplate,
        handleEditTemplate,
        handleEditLetter,
        issuedLogs,
    } = useLetterhead();
    const { userDetails } = useUserDetailContext();
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");


    // Reset filters when navigating between tabs
    useEffect(() => {
        setSearchTerm("");
        setStatusFilter("ALL");

    }, [activeTab]);

    useEffect(() => {
        // console.log("Before LetterKead")
        dispatch(getLetters(userDetails?.reference_id));
        //  console.log("IAMRESPONSE", res)
        //   console.log("After LetterKead")
    }, [])

    const getTemplateData = (template) => {
        if (!template) return { color: "#f1f5f9", logo: "", companyName: "Unknown" };

        const components = template?.TemplateComponents || template?.components || [];
        const header = components?.find(c =>
            c?.component_type === "header" || c?.type === "header"
        );

        let color = "#f1f5f9";
        let logo = "";

        if (header) {
            const propsRaw = header?.TemplateProperties || header.properties || [];
            if (Array.isArray(propsRaw)) {
                const colorProp = propsRaw.find(p => p.property_key === 'backgroundColor');
                const logoProp = propsRaw.find(p => p.property_key === 'logoUrl');
                if (colorProp) color = colorProp.property_value;
                if (logoProp) logo = logoProp.property_value;
            } else if (typeof propsRaw === 'object') {
                color = propsRaw.backgroundColor || color;
                logo = propsRaw.logoUrl || logo;
            }
        }

        let specificName = null;
        const identities = template?.TemplateIdentities || template?.template_identity_fields || template?.identityFields || [];

        if (Array.isArray(identities) && identities?.length > 0) {
            const nameField = identities?.find(f => f.field_key === 'companyName' || f.key === 'companyName');
            if (nameField) {
                specificName = nameField.field_value || nameField.value;
            }
        }

        const companyName = specificName || "Unknown Company";
        return { color, logo, companyName };
    };

    const getLetterTemplate = (letter) => {
        if (!letter || !templates) return null;
        return templates.find(t => String(t.template_id) === String(letter.template_id));
    };

    // const filteredTemplates = (templates)?.filter((t) => {
    //     const companyName = getTemplateData(t).companyName.toLowerCase();
    //     const templateName = (t.template_name || "").toLowerCase();
    //     const searchLower = searchTerm.toLowerCase();

    //     const matchesSearch = templateName.includes(searchLower) || companyName.includes(searchLower);
    //     const matchesStatus = statusFilter === "ALL" || (t.status || "").toUpperCase() === statusFilter;

    //     return matchesSearch && matchesStatus;
    // }) || null;

    // const filteredLetters = (letters)?.filter((l) => {
    //     const subject = (l.subject || "").toLowerCase();
    //     const recipientName = (l.letter_recipients?.[0]?.name || "").toLowerCase();
    //     const searchLower = searchTerm.toLowerCase();

    //     const matchesSearch = subject.includes(searchLower) || recipientName.includes(searchLower);
    //     const matchesStatus = statusFilter === "ALL" || (l.status || "").toUpperCase() === statusFilter;

    //     return matchesSearch && matchesStatus;
    // }) || null;

    const filteredTemplates = useMemo(() => {
        // Ensure templates is an array, default to empty array if not
        const safeTemplates = Array.isArray(templates) ? templates : [];

        return safeTemplates.filter((t) => {
            const companyName = getTemplateData(t).companyName.toLowerCase();
            const templateName = (t.template_name || "").toLowerCase();
            const searchLower = searchTerm.toLowerCase();

            const matchesSearch = templateName.includes(searchLower) || companyName.includes(searchLower);
            const matchesStatus = statusFilter === "ALL" || (t.status || "").toUpperCase() === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [templates, searchTerm, statusFilter]);

    const filteredLetters = useMemo(() => {
        // Ensure letters is an array, default to empty array if not
        const safeLetters = Array.isArray(letters) ? letters : [];

        return safeLetters.filter((l) => {
            const subject = (l.subject || "").toLowerCase();
            const recipientName = (l.letter_recipients?.[0]?.name || "").toLowerCase();
            const searchLower = searchTerm.toLowerCase();

            const matchesSearch = subject.includes(searchLower) || recipientName.includes(searchLower);
            const matchesStatus = statusFilter === "ALL" || (l.status || "").toUpperCase() === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [letters, searchTerm, statusFilter]);

    return (<div className="d-flex flex-column min-vh-100 bg-light">
        <div className="px-4 px-lg-4 py-4 py-lg-4">
            <h1 className="display-6 fw-bold text-dark mb-3">
                Create & Manage Letterheads
            </h1>
            <p className="small text-secondary mb-4" style={{ maxWidth: "800px" }}>
                Design professional letterhead templates, compose letters, and
                download them as PDFs. All in one place.
            </p>

            {/* ACTION CARDS - Keep these as is */}
            <div className="row g-4 mb-5">
                <div className="col-md-6">
                    <button
                        onClick={() => handleNavigate("/create-template")}
                        className="btn btn-outline-primary w-100 text-start p-4 rounded-3 bg-white d-flex align-items-center gap-3 shadow-sm hover-shadow transition"
                        style={{ borderWidth: "2px" }}
                    >
                        <div className="bg-primary bg-opacity-10 rounded-3 d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px" }}>
                            <span className="fs-2 text-primary">+</span>
                        </div>
                        <div>
                            <h5 className="fw-semibold mb-1">Create Template</h5>
                            <p className="text-secondary mb-0 small">Design a new letterhead template</p>
                        </div>
                    </button>
                </div>

                <div className="col-md-6">
                    <button
                        onClick={() => handleNavigate("/compose-letter")}
                        className="btn btn-outline-success w-100 text-start p-4 rounded-3 bg-white d-flex align-items-center gap-3 shadow-sm hover-shadow transition"
                        style={{ borderWidth: "2px" }}
                    >
                        <div className="bg-success bg-opacity-10 rounded-3 d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px" }}>
                            <span className="fs-2 text-success">+</span>
                        </div>
                        <div>
                            <h5 className="fw-semibold mb-1">Compose Letter</h5>
                            <p className="text-secondary mb-0 small">Write a new letter</p>
                        </div>
                    </button>
                </div>
            </div>

            {/* TABS & SEARCH CONTAINER - Keep as is */}
            <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-end border-bottom pb-0 mb-4 gap-3">
                {/* Left Side: Tabs */}
                <div className="d-flex gap-4">
                    <button
                        onClick={() => setActiveTab("templates")}
                        className={`btn btn-link text-decoration-none p-0 pb-2 fw-medium position-relative ${activeTab === "templates" ? "text-primary" : "text-secondary"}`}
                    >
                        Templates ({templates?.length || 0})
                        {activeTab === "templates" && (
                            <span className="position-absolute bottom-0 start-0 w-100 bg-primary" style={{ height: "2px" }} />
                        )}
                    </button>

                    <button
                        onClick={() => setActiveTab("letters")}
                        className={`btn btn-link text-decoration-none p-0 pb-2 fw-medium position-relative ${activeTab === "letters" ? "text-primary" : "text-secondary"}`}
                    >
                        Letters ({letters?.length || 0})
                        {activeTab === "letters" && (
                            <span className="position-absolute bottom-0 start-0 w-100 bg-primary" style={{ height: "2px" }} />
                        )}
                    </button>

                    <button
                        onClick={() => handleNavigate("/audit-logs")}
                        className="btn btn-link text-decoration-none p-0 pb-2 fw-medium text-secondary d-flex align-items-center gap-2 hover-text-primary transition"
                    >
                        Issued Letters
                        <span className="small text-secondary fw-medium">
                            ({issuedLogs?.length || 0})
                        </span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-60">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                    </button>
                </div>

                {/* Right Side: Search and Filter */}
                <div className="d-flex flex-column flex-sm-row align-items-center gap-3">
                    <div className="position-relative w-100" style={{ minWidth: "280px" }}>
                        <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary" size={16} />
                        <input
                            type="text"
                            placeholder={activeTab === "templates" ? "Search templates..." : "Search subjects & recipients..."}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="form-control ps-5 py-2"
                            style={{ fontSize: "0.875rem" }}
                        />
                    </div>

                    {activeTab === "letters" && (
                        <div className="position-relative w-100" style={{ minWidth: "180px" }}>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="form-select py-2 appearance-none"
                                style={{ fontSize: "0.875rem" }}
                            >
                                <option value="ALL">All Statuses</option>
                                <option value="DRAFT">Draft</option>
                                <option value="PENDING_SIGNATURE">Pending Signature</option>
                                <option value="SIGNED">Signed</option>
                                <option value="SENT">Sent</option>
                            </select>
                            <div className="position-absolute top-50 end-0 translate-middle-y me-3 pointer-events-none text-secondary">
                                <svg
                                    className="w-100"
                                    style={{ width: "16px", height: "16px" }}
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* CONTENT - UPDATED WITH SMALLER CARDS */}
            <div className="mt-4 mt-lg-5">
                {activeTab === "templates" && (
                    <>
                        {isLoading && <p className="text-secondary">Loading templates...</p>}
                        {!isLoading && (
                            <div className="row g-3">
                                {filteredTemplates && filteredTemplates?.length > 0 ? (
                                    filteredTemplates?.map((template) => {
                                        const { color, logo, companyName } = getTemplateData(template);
                                        return (
                                            <div key={template.template_id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                                                <TemplateCard
                                                    id={template.template_id}
                                                    title={template.template_name}
                                                    subtitle={companyName}
                                                    headerColor={color}
                                                    logoUrl={logo}
                                                    createdAt={template.created_at ? new Date(template.created_at).toLocaleDateString() : "Just now"}
                                                    onEdit={handleEditTemplate}
                                                    onDelete={handleDeleteTemplate}
                                                />
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="col-12">
                                        <div className="text-center py-5 bg-white rounded-3 border border-secondary border-opacity-25 border-dashed">
                                            <p className="text-secondary fw-medium mb-0">No templates match your search criteria.</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* LETTERS TAB - UPDATED WITH SMALLER CARDS */}
                {activeTab === "letters" && (
                    <>
                        {isLoading && <p className="text-secondary">Loading letters...</p>}
                        {!isLoading && (
                            <div className="row g-3">
                                {filteredLetters && filteredLetters?.length > 0 ? (
                                    filteredLetters?.map((l) => (
                                        <div key={l?.letter_id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                                            <LetterCard
                                                letter={l}
                                                onView={handleViewLetter}
                                                onDelete={handleDeleteLetter}
                                                onEdit={handleEditLetter}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <div className="col-12">
                                        <div className="text-center py-5 bg-white rounded-3 border border-secondary border-opacity-25 border-dashed">
                                            <p className="text-secondary fw-medium mb-0">No letters match your search criteria.</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>

        {selectedLetter && (
            <LetterViewModal
                letter={selectedLetter}
                onClose={handleCloseModal}
                template={getLetterTemplate(selectedLetter)}
            />
        )}
    </div>
    );
};

export default LetterheadMainPage;